import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import PricingFAQSection from './PricingFAQSection';
import ExecutiveDashboardPanelCTA from './ExecutiveDashboardPanelCTA';
import { Check } from 'lucide-react';

const PricingPage = () => {
  const navigate = useNavigate();

  const handleGetStarted = (planType) => {
    // Navigate to contact page with plan information
    navigate('/contact', { state: { selectedPlan: planType } });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <p className="text-orange-500 font-medium mb-3 sm:mb-4 text-sm sm:text-base">Pricing Plans</p>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Simple pricing to get you started
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Practice management software is expensive, and the last thing you need is to 
              double that cost. Don't worry, we've got you covered with our affordable pricing options:
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {/* Management Plan */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6 sm:p-8 hover:border-orange-200 transition-colors">
              <div className="mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Management</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Designed for senior management who wants to maintain control over the production process while 
                  keeping sensitive financial data private and not shared across teams. This plan ensures privacy and focused 
                  control of operations without the complexity of financial reporting.
                </p>
                <div className="flex items-baseline mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-gray-900">$99</span>
                  <span className="text-gray-600 ml-2 text-sm sm:text-base">/month</span>
                </div>
                <button 
                  onClick={() => handleGetStarted('Management')}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors min-h-[44px]"
                >
                  Get Started
                </button>
              </div>
              
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Firm overview</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Timesheet tracking</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Service item analysis</span>
                </li>
              </ul>
            </div>
            
            {/* Executive Plan */}
            <div className="bg-white rounded-lg border-2 border-orange-200 p-6 sm:p-8 relative hover:border-orange-300 transition-colors">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-500 text-white px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm font-medium">
                  Most Popular
                </span>
              </div>
              
              <div className="mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Executive</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Full visibility of your firm with detailed profitability reports and margin analysis by person, team, and client. 
                  Recommended for executive users looking to automate financial analysis and eliminate the need for dozens of 
                  manual Excel reports.
                </p>
                <div className="flex items-baseline mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl font-bold text-gray-900">$199</span>
                  <span className="text-gray-600 ml-2 text-sm sm:text-base">/month</span>
                </div>
                <button 
                  onClick={() => handleGetStarted('Executive')}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors min-h-[44px]"
                >
                  Get Started
                </button>
              </div>
              
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Everything in Management</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Billing analysis</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Team member production analysis</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Client profitability reports</span>
                </li>
              </ul>
            </div>
            
            {/* Enterprise Plan */}
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6 sm:p-8 hover:border-blue-200 transition-colors">
              <div className="mb-4 sm:mb-6">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Enterprise</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  For Multi-office CPA firms or those with 50–100+ staff who want more than standard dashboards — including advanced profitability analysis, integrations, and regular customization.
                </p>
                <div className="flex items-baseline mb-4 sm:mb-6">
                  <span className="text-2xl sm:text-3xl font-bold text-gray-900">Custom</span>
                </div>
                <button 
                  onClick={() => handleGetStarted('Enterprise')}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors min-h-[44px]"
                >
                  Contact Us
                </button>
              </div>
              

              
              <ul className="space-y-2 sm:space-y-3">
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Everything in Executive</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Advanced integrations: QBS Online, CRM, Spreadsheets, etc.</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Custom KPIs and Development</span>
                </li>
                <li className="flex items-center">
                  <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <span className="text-sm sm:text-base text-gray-700">Hands-on support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
      
      <ExecutiveDashboardPanelCTA />
      
      <PricingFAQSection />
      
      <Footer />
    </div>
  );
};

export default PricingPage;